import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Calendar, ToggleLeft, ToggleRight, Trash2, Edit2, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicBatchesManager = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    is_active: true,
    go_live_at: '',
    code_format: 'AAA-AAA'
  });
  const [formatPreview, setFormatPreview] = useState([]);
  const [maxCombinations, setMaxCombinations] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relic/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setLoading(false);
    }
  };

  const calculateCombinations = async (format) => {
    if (!format || format.length === 0) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/relic/codes/calculate`,
        { code_format: format },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setMaxCombinations(response.data);
    } catch (error) {
      console.error('Error calculating combinations:', error);
      setMaxCombinations(null);
    }
  };

  const previewFormat = (format) => {
    // Generate sample preview locally
    const letters = "ABCDEFGHJKMNPQRSTUVWXYZ";
    const digits = "23456789";
    const charset = letters + digits;
    
    const samples = [];
    for (let i = 0; i < 5; i++) {
      let sample = '';
      for (let char of format) {
        if (char === 'A') {
          sample += letters[Math.floor(Math.random() * letters.length)];
        } else if (char === '0') {
          sample += digits[Math.floor(Math.random() * digits.length)];
        } else if (char === '#') {
          sample += charset[Math.floor(Math.random() * charset.length)];
        } else {
          sample += char;
        }
      }
      samples.push(sample);
    }
    setFormatPreview(samples);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        go_live_at: formData.go_live_at ? new Date(formData.go_live_at).toISOString() : null
      };

      if (editingBatch) {
        await axios.patch(
          `${API_URL}/api/admin/relic/batches/${editingBatch.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(`${API_URL}/api/admin/relic/batches`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowCreateModal(false);
      setEditingBatch(null);
      setFormData({ name: '', notes: '', is_active: true, go_live_at: '', code_format: 'AAA-AAA' });
      setFormatPreview([]);
      setMaxCombinations(null);
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      alert('Failed to save batch');
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({
      name: batch.name,
      notes: batch.notes || '',
      is_active: batch.is_active,
      go_live_at: batch.go_live_at ? new Date(batch.go_live_at).toISOString().slice(0, 16) : '',
      code_format: batch.code_format || 'AAA-AAA'
    });
    setShowCreateModal(true);
    calculateCombinations(batch.code_format || 'AAA-AAA');
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Delete this batch and all its codes? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/admin/relic/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBatches();
    } catch (error) {
      console.error('Error deleting batch:', error);
      alert('Failed to delete batch');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Relic Batches</h1>
        <button
          onClick={() => {
            setEditingBatch(null);
            setFormData({ name: '', notes: '', is_active: true, go_live_at: '', code_format: 'AAA-AAA' });
            setFormatPreview([]);
            setMaxCombinations(null);
            setShowCreateModal(true);
            previewFormat('AAA-AAA');
            calculateCombinations('AAA-AAA');
          }}
          className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
        >
          <Plus className="w-4 h-4" />
          <span>New Batch</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {batches.map((batch) => (
          <div key={batch.id} className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-bold text-lg">{batch.name}</h3>
                {batch.notes && (
                  <p className="text-sm text-gray-600 mt-1">{batch.notes}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(batch)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(batch.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`flex items-center ${batch.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                  {batch.is_active ? <ToggleRight className="w-4 h-4 mr-1" /> : <ToggleLeft className="w-4 h-4 mr-1" />}
                  {batch.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {batch.go_live_at && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Go Live:</span>
                  <span className="flex items-center text-gray-900">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(batch.go_live_at).toLocaleDateString()}
                  </span>
                </div>
              )}

              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Total Codes:</span>
                  <span className="font-semibold">{batch.total_codes || 0}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Redeemed:</span>
                  <span className="font-semibold">{batch.redeemed_count || 0} ({batch.redeemed_percentage || 0}%)</span>
                </div>
              </div>

              <div className="border-t pt-2 mt-2">
                <div className="text-xs text-gray-600 mb-1">By Rarity:</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Common:</span>
                    <span className="font-medium">{batch.rarity_counts?.COMMON || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Uncommon:</span>
                    <span className="font-medium">{batch.rarity_counts?.UNCOMMON || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rare:</span>
                    <span className="font-medium">{batch.rarity_counts?.RARE || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Legendary:</span>
                    <span className="font-medium">{batch.rarity_counts?.LEGENDARY || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingBatch ? 'Edit Batch' : 'Create New Batch'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Batch Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Go Live Date</label>
                <input
                  type="datetime-local"
                  value={formData.go_live_at}
                  onChange={(e) => setFormData({ ...formData, go_live_at: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Code Format *
                </label>
                <input
                  type="text"
                  value={formData.code_format}
                  onChange={(e) => {
                    const newFormat = e.target.value.toUpperCase();
                    setFormData({ ...formData, code_format: newFormat });
                    previewFormat(newFormat);
                    calculateCombinations(newFormat);
                  }}
                  className="w-full border rounded-lg px-3 py-2 font-mono"
                  placeholder="AAA-AAA"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  A = Letter, 0 = Digit, # = Any, - = Separator
                </p>
                {formatPreview.length > 0 && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <div className="text-xs font-medium text-gray-700 mb-1">Preview:</div>
                    <div className="flex flex-wrap gap-2">
                      {formatPreview.map((code, idx) => (
                        <span key={idx} className="font-mono text-sm bg-white px-2 py-1 rounded border">
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {maxCombinations && (
                  <div className="mt-2 text-xs text-blue-600 font-medium">
                    Max unique codes: {maxCombinations.formatted}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm font-medium">Active</label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingBatch(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  {editingBatch ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
